import { Navigate, Outlet, Path, To, useLocation } from "react-router-dom"
import { useAuth } from "@app/hooks"
import { ErrorPage } from "@core/pages"
import { App } from "@app/interfaces"
import { hasAccessPolicy, hasModuleAccess } from "@app/utils"

interface IPrivateRouteProps {
  needRole: number
  needModule?: App.ModuleName // Новое поле для модульной проверки
  accessPolicy?: App.AccessPolicy
  exactRole?: boolean
  redirectTo?: To
  enableComeBackRedirect?: boolean
}
interface IPrivateRouteChildrenProps extends IPrivateRouteProps {
  children?: React.ReactElement
}
interface IPrivateRouteElementProps extends IPrivateRouteProps {
  element?: React.ReactElement
}
type PrivateRouteProps = IPrivateRouteChildrenProps | IPrivateRouteElementProps

export function PrivateRoute({
  redirectTo,
  needRole = 0,
  needModule,
  accessPolicy,
  exactRole = false,
  enableComeBackRedirect,
  ...props
}: PrivateRouteProps) {
  const auth = useAuth()

  const { pathname, search } = useLocation()

  const searchParams = new URLSearchParams(search)
  const comeBack = searchParams.get("redirectTo")

  let hasAccess = false

  // Именованная политика заменяет старые условия только у помеченных маршрутов.
  if (accessPolicy !== undefined) {
    if (!auth?.token?.trim()) return <Navigate to="/login" replace />
    hasAccess = hasAccessPolicy(accessPolicy, auth)
  }
  // Для OIDC авторизации проверяем модульные роли, если указан модуль
  else if (auth?.type === App.AuthType.OIDC && needModule) {
    const userInfo = auth.userInfo as App.OIDCUserInfo
    const userRoles = userInfo?.roles || []
    hasAccess = hasModuleAccess(userRoles, needModule)
  }
  // Для STANDARD авторизации или если модуль не указан - используем старую логику
  else {
    hasAccess = !(
      needRole > (auth?.role ?? App.UserRole.ANON) ||
      (exactRole && needRole !== (auth?.role ?? App.UserRole.ANON))
    )
  }

  if (!hasAccess) {
    if (accessPolicy === undefined && redirectTo) {
      let to: Partial<Path>
      if (typeof redirectTo === "string") {
        to = {
          pathname: redirectTo,
        }
      } else {
        to = {
          ...redirectTo,
        }
      }
      if (enableComeBackRedirect) {
        if (to.search) {
          to.search += `&redirectTo=${pathname}`
        } else {
          to.search = `redirectTo=${pathname}`
        }
      }
      return <Navigate to={comeBack ?? to} />
    }
    return (
      <ErrorPage code={403} text="Недостаточно прав для просмотра страницы" />
    )
  }

  const element = ("children" in props && props.children) ||
    ("element" in props && props.element) || <Outlet />
  return element
}
